import { rng, type Decor, type Sign } from './world';

// Drawing the scenery. Everything is in world coordinates (the camera's
// transform is already applied) and outlined in the same dark ink as the fish
// art, so the world and the creatures look like one drawing.

const INK = '#1e2633';
const CORAL = ['#ff7a8a', '#ff9f4a', '#b77cff', '#ffd166', '#ff6fb5', '#5ed6c9'];
const SPONGE = ['#ffb347', '#e86a92', '#9b8cff', '#7bd389', '#ff8c61', '#f6d55c'];

/** Rough size, for skipping things off screen. */
export function decorReach(d: Decor): number {
  return d.kind === 'kelp' ? 900 * d.s : d.kind === 'mast' ? 420 : d.kind === 'pillar' ? 240 * d.s : 160 * d.s;
}

export function drawDecor(g: CanvasRenderingContext2D, d: Decor, t: number, lights: [number, number, number, string][]) {
  const r = rng(d.seed);
  g.save();
  g.translate(d.x, d.y);
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.strokeStyle = INK;
  g.lineWidth = 2.2;
  const s = d.s;
  switch (d.kind) {
    case 'grass': {
      const n = 3 + Math.floor(r() * 4);
      for (let i = 0; i < n; i++) {
        const h = (40 + r() * 50) * s, x = (r() - 0.5) * 26 * s;
        const sway = Math.sin(t * 1.3 + d.seed + i) * 8 * s;
        g.strokeStyle = i % 2 ? '#3f9b5f' : '#2f8150';
        g.lineWidth = 4 * s;
        g.beginPath(); g.moveTo(x, 4); g.quadraticCurveTo(x + sway * 0.4, -h * 0.5, x + sway, -h); g.stroke();
      }
      break;
    }
    case 'rock': {
      g.fillStyle = ['#7d8794', '#6b7480', '#8b8f7d'][d.color % 3];
      g.beginPath();
      const w = (30 + r() * 30) * s, h = (18 + r() * 18) * s;
      g.moveTo(-w, 6); g.quadraticCurveTo(-w * 0.9, -h, 0, -h * 1.1); g.quadraticCurveTo(w * 0.9, -h, w, 6); g.closePath();
      g.fill(); g.stroke();
      break;
    }
    case 'star': {
      g.fillStyle = ['#ff8a5c', '#ffcf5c', '#ff6f91'][d.color % 3];
      g.rotate(r() * 0.6 - 0.3);
      g.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5, rad = (i % 2 ? 6 : 15) * s;
        g.lineTo(Math.cos(a) * rad, Math.sin(a) * rad - 8 * s);
      }
      g.closePath(); g.fill(); g.stroke();
      break;
    }
    case 'shell': {
      g.fillStyle = ['#ffe3c4', '#ffd0dc', '#f3e7b0'][d.color % 3];
      g.beginPath(); g.moveTo(-12 * s, 2); g.quadraticCurveTo(0, -24 * s, 12 * s, 2); g.closePath(); g.fill(); g.stroke();
      for (const x of [-5, 0, 5]) { g.beginPath(); g.moveTo(0, 0); g.lineTo(x * s, -14 * s); g.stroke(); }
      break;
    }
    case 'branch': {
      g.strokeStyle = CORAL[d.color];
      const grow = (x: number, y: number, a: number, len: number, depth: number) => {
        const x2 = x + Math.cos(a) * len, y2 = y + Math.sin(a) * len;
        g.lineWidth = Math.max(3, depth * 3.4) * s;
        g.beginPath(); g.moveTo(x, y); g.lineTo(x2, y2); g.stroke();
        if (depth > 1) { grow(x2, y2, a - 0.45 - r() * 0.2, len * 0.75, depth - 1); grow(x2, y2, a + 0.45 + r() * 0.2, len * 0.75, depth - 1); }
        else { g.fillStyle = CORAL[d.color]; g.beginPath(); g.arc(x2, y2, 3.5 * s, 0, Math.PI * 2); g.fill(); }
      };
      grow(0, 4, -Math.PI / 2 + (r() - 0.5) * 0.3, 34 * s, 3);
      break;
    }
    case 'fan': {
      const sway = Math.sin(t * 0.9 + d.seed) * 0.05;
      g.rotate(sway);
      g.scale(0.7, 0.7);
      g.fillStyle = CORAL[d.color] + 'cc';
      g.beginPath(); g.moveTo(0, 4); g.bezierCurveTo(-60 * s, -40 * s, -50 * s, -95 * s, 0, -100 * s);
      g.bezierCurveTo(50 * s, -95 * s, 60 * s, -40 * s, 0, 4); g.fill(); g.stroke();
      g.strokeStyle = 'rgba(30,38,51,0.45)'; g.lineWidth = 1.2;
      for (let i = -3; i <= 3; i++) { g.beginPath(); g.moveTo(0, 0); g.lineTo(i * 14 * s, -90 * s + Math.abs(i) * 8 * s); g.stroke(); }
      break;
    }
    case 'brain': {
      g.fillStyle = ['#e9c46a', '#b5d36b', '#f4a261'][d.color % 3];
      g.beginPath(); g.ellipse(0, -16 * s, 34 * s, 22 * s, 0, Math.PI, 0); g.lineTo(34 * s, 4); g.lineTo(-34 * s, 4); g.closePath(); g.fill(); g.stroke();
      g.lineWidth = 1.5;
      for (let i = 0; i < 4; i++) { g.beginPath(); g.moveTo(-26 * s + i * 14 * s, -4 * s); g.quadraticCurveTo(-20 * s + i * 14 * s, -30 * s, -12 * s + i * 14 * s, -6 * s); g.stroke(); }
      break;
    }
    case 'anemone': {
      const col = ['#ff6fb5', '#c77dff', '#ff9f4a'][d.color % 3];
      g.fillStyle = '#7a4a6a';
      g.beginPath(); g.ellipse(0, -6 * s, 16 * s, 12 * s, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.strokeStyle = col; g.lineWidth = 5 * s;
      for (let i = 0; i < 9; i++) {
        const a = -Math.PI + (i / 8) * Math.PI, sway = Math.sin(t * 2 + i + d.seed) * 0.25;
        g.beginPath(); g.moveTo(Math.cos(a) * 10 * s, -10 * s);
        g.quadraticCurveTo(Math.cos(a + sway) * 26 * s, -24 * s, Math.cos(a + sway * 1.5) * 30 * s, -40 * s + Math.abs(Math.cos(a)) * 14 * s);
        g.stroke();
      }
      break;
    }
    case 'sponge': {
      g.fillStyle = SPONGE[d.color];
      for (let i = 0; i < 3; i++) {
        const x = (i - 1) * 16 * s, h = (40 + r() * 40) * s;
        g.beginPath(); g.roundRect(x - 7 * s, -h, 14 * s, h + 4, 7 * s); g.fill(); g.stroke();
        g.fillStyle = 'rgba(30,38,51,0.35)'; g.beginPath(); g.ellipse(x, -h + 4 * s, 4 * s, 2.5 * s, 0, 0, Math.PI * 2); g.fill();
        g.fillStyle = SPONGE[d.color];
      }
      break;
    }
    case 'kelp': {
      const h = (520 + r() * 380) * s;
      const seg = 9;
      let px = 0, py = 4;
      g.strokeStyle = '#2f7d4a'; g.lineWidth = 7 * s;
      g.beginPath(); g.moveTo(0, 4);
      const pts: [number, number][] = [];
      for (let i = 1; i <= seg; i++) {
        const k = i / seg;
        const x = Math.sin(t * 0.7 + d.seed * 0.01 + k * 3) * 26 * k * s + Math.sin(k * 5 + d.seed) * 10 * s;
        const y = 4 - h * k;
        g.quadraticCurveTo(px, py, (px + x) / 2, (py + y) / 2);
        px = x; py = y; pts.push([x, y]);
      }
      g.stroke();
      g.fillStyle = '#4a9e5c';
      for (let i = 1; i < pts.length; i += 1) {
        const [x, y] = pts[i]; const side = i % 2 ? 1 : -1;
        g.save(); g.translate(x, y); g.rotate(side * (0.9 + Math.sin(t + i) * 0.15));
        g.beginPath(); g.ellipse(0, -20 * s, 9 * s, 24 * s, 0, 0, Math.PI * 2); g.fill(); g.stroke(); g.restore();
      }
      break;
    }
    case 'crystal': {
      const col = ['#7ef9ff', '#c58bff', '#8affc1'][d.color % 3];
      const pulse = 0.75 + 0.25 * Math.sin(t * 1.5 + d.seed);
      lights.push([d.x, d.y - 30 * s, 130 * s * pulse, col]);
      for (let i = 0; i < 3; i++) {
        const a = (i - 1) * 0.45 + (r() - 0.5) * 0.2, h = (50 + r() * 40) * s * (i === 1 ? 1.2 : 0.8), w = 11 * s;
        g.save(); g.rotate(a);
        g.fillStyle = col;
        g.beginPath(); g.moveTo(-w, 4); g.lineTo(-w, -h * 0.8); g.lineTo(0, -h); g.lineTo(w, -h * 0.8); g.lineTo(w, 4); g.closePath();
        g.globalAlpha = 0.9; g.fill(); g.globalAlpha = 1; g.stroke();
        g.strokeStyle = 'rgba(255,255,255,0.7)'; g.lineWidth = 1.5;
        g.beginPath(); g.moveTo(-w * 0.4, -4); g.lineTo(-w * 0.4, -h * 0.7); g.stroke();
        g.restore();
        g.strokeStyle = INK; g.lineWidth = 2.2;
      }
      break;
    }
    case 'stalactite': {
      g.fillStyle = '#5b5f6e';
      for (let i = 0; i < 3; i++) {
        const x = (i - 1) * 18 * s, h = (30 + r() * 50) * s;
        g.beginPath(); g.moveTo(x - 9 * s, -6); g.lineTo(x, h); g.lineTo(x + 9 * s, -6); g.closePath(); g.fill(); g.stroke();
      }
      break;
    }
    case 'mast': {
      g.rotate(-0.35);
      g.fillStyle = '#7a5434';
      g.beginPath(); g.roundRect(-9, -380, 18, 384, 6); g.fill(); g.stroke();
      g.beginPath(); g.roundRect(-110, -300, 220, 14, 5); g.fill(); g.stroke();
      g.fillStyle = 'rgba(235,225,200,0.55)';
      g.beginPath(); g.moveTo(-100, -286); g.quadraticCurveTo(-40, -180, -95, -120); g.lineTo(60, -150); g.quadraticCurveTo(30, -220, 100, -286); g.closePath(); g.fill(); g.stroke();
      break;
    }
    case 'barrel': {
      g.fillStyle = '#8a5a35';
      g.beginPath(); g.roundRect(-22 * s, -50 * s, 44 * s, 54 * s, 10 * s); g.fill(); g.stroke();
      g.strokeStyle = '#4b3a2a'; g.lineWidth = 3;
      for (const y of [-38, -12]) { g.beginPath(); g.moveTo(-22 * s, y * s); g.lineTo(22 * s, y * s); g.stroke(); }
      break;
    }
    case 'anchor': {
      g.rotate(0.5);
      g.strokeStyle = '#5c6670'; g.lineWidth = 9;
      g.beginPath(); g.moveTo(0, -90); g.lineTo(0, 0); g.moveTo(-40, -20); g.quadraticCurveTo(0, 30, 40, -20); g.moveTo(-22, -70); g.lineTo(22, -70); g.stroke();
      g.lineWidth = 3; g.strokeStyle = INK; g.beginPath(); g.arc(0, -100, 10, 0, Math.PI * 2); g.stroke();
      break;
    }
    case 'vent': {
      g.fillStyle = '#4a4550';
      g.beginPath(); g.moveTo(-34 * s, 4); g.lineTo(-14 * s, -60 * s); g.lineTo(14 * s, -60 * s); g.lineTo(34 * s, 4); g.closePath(); g.fill(); g.stroke();
      lights.push([d.x, d.y - 60 * s, 90 * s, '#ff9a55']);
      g.fillStyle = 'rgba(255,255,255,0.5)';
      for (let i = 0; i < 6; i++) {
        const p = ((t * 0.5 + i / 6) % 1);
        g.beginPath(); g.arc(Math.sin(p * 9 + i) * 10 * s, -60 * s - p * 260 * s, (3 + i % 3) * s, 0, Math.PI * 2); g.globalAlpha = 1 - p; g.fill();
      }
      g.globalAlpha = 1;
      break;
    }
    case 'worms': {
      for (let i = 0; i < 5; i++) {
        const x = (i - 2) * 9 * s, h = (30 + r() * 40) * s;
        g.strokeStyle = '#e8e3d6'; g.lineWidth = 6 * s;
        g.beginPath(); g.moveTo(x, 4); g.lineTo(x, -h); g.stroke();
        g.fillStyle = '#ff3d5a'; g.beginPath(); g.arc(x, -h - 5 * s + Math.sin(t * 2 + i) * 2, 6 * s, 0, Math.PI * 2); g.fill();
      }
      break;
    }
    case 'pillar': {
      // A broken temple column.
      const h = (150 + r() * 60) * s, w = 26 * s;
      g.fillStyle = '#7f9a96';
      g.beginPath(); g.rect(-w / 2, -h, w, h); g.fill(); g.stroke();
      g.strokeStyle = 'rgba(30,38,51,0.35)'; g.lineWidth = 1.5;
      for (const fx of [-w / 4, 0, w / 4]) { g.beginPath(); g.moveTo(fx, -h + 8); g.lineTo(fx, -8); g.stroke(); }
      g.strokeStyle = INK; g.lineWidth = 2.2; g.fillStyle = '#9db5b1';
      g.beginPath(); g.rect(-w * 0.8, -10, w * 1.6, 12); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(-w * 0.8, -h); g.lineTo(-w * 0.2, -h - 10 * s); g.lineTo(w * 0.3, -h - 4 * s); g.lineTo(w * 0.8, -h - 12 * s); g.lineTo(w * 0.8, -h); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#3f7a4a';
      g.beginPath(); g.ellipse(w * 0.2, -h * 0.55, 6 * s, 14 * s, 0.3, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'urn': {
      // An old clay urn with a painted wave.
      g.fillStyle = ['#c77c48', '#b4653a', '#d49a5c'][d.color % 3];
      g.beginPath();
      g.moveTo(-10 * s, -54 * s); g.lineTo(10 * s, -54 * s); g.lineTo(8 * s, -46 * s);
      g.bezierCurveTo(30 * s, -40 * s, 30 * s, -6 * s, 12 * s, 2); g.lineTo(-12 * s, 2);
      g.bezierCurveTo(-30 * s, -6 * s, -30 * s, -40 * s, -8 * s, -46 * s); g.closePath(); g.fill(); g.stroke();
      g.strokeStyle = '#2b2b3a'; g.lineWidth = 2;
      g.beginPath(); for (let x = -18; x <= 18; x += 3) g.lineTo(x * s, (-26 + Math.sin(x / 3) * 3) * s); g.stroke();
      break;
    }
    case 'glow': {
      const pulse = 0.5 + 0.5 * Math.sin(t * 1.2 + d.seed);
      lights.push([d.x, d.y - 40, 70 * pulse + 20, '#7af7ff']);
      g.fillStyle = `rgba(122,247,255,${0.5 + pulse * 0.4})`;
      for (let i = 0; i < 5; i++) { g.beginPath(); g.arc((r() - 0.5) * 60, -20 - r() * 60, 2 + r() * 3, 0, Math.PI * 2); g.fill(); }
      break;
    }
  }
  g.restore();
}

export function drawSign(g: CanvasRenderingContext2D, s: Sign, t: number, near: boolean) {
  g.save();
  g.translate(s.x, s.y);
  g.lineJoin = 'round';
  g.strokeStyle = INK;
  g.lineWidth = 2.5;
  if (s.style === 'post') {
    g.fillStyle = '#9b6b43';
    g.beginPath(); g.roundRect(-5, -70, 10, 76, 3); g.fill(); g.stroke();
    g.fillStyle = '#c4935e';
    g.beginPath(); g.roundRect(-38, -100, 76, 40, 6); g.fill(); g.stroke();
    g.strokeStyle = 'rgba(30,38,51,0.5)'; g.lineWidth = 2;
    for (const y of [-88, -78, -69]) { g.beginPath(); g.moveTo(-26, y); g.lineTo(26, y); g.stroke(); }
  } else if (s.style === 'stone') {
    g.fillStyle = '#8e8a9c';
    g.beginPath(); g.moveTo(-34, 6); g.lineTo(-30, -70); g.quadraticCurveTo(0, -96, 30, -70); g.lineTo(34, 6); g.closePath(); g.fill(); g.stroke();
    g.strokeStyle = '#7ef9ff'; g.lineWidth = 2;
    for (const y of [-58, -44, -30]) { g.beginPath(); g.moveTo(-18, y); g.lineTo(18, y); g.stroke(); }
  } else {
    g.rotate(-0.5 + Math.sin(t) * 0.05);
    g.fillStyle = 'rgba(160,220,200,0.75)';
    g.beginPath(); g.roundRect(-30, -22, 52, 26, 12); g.fill(); g.stroke();
    g.beginPath(); g.roundRect(20, -15, 16, 12, 3); g.fillStyle = '#8a5a35'; g.fill(); g.stroke();
    g.fillStyle = '#f2e6c4'; g.beginPath(); g.roundRect(-20, -15, 30, 10, 3); g.fill();
  }
  if (near) {
    const b = Math.sin(t * 4) * 4;
    g.fillStyle = '#fff'; g.strokeStyle = INK; g.lineWidth = 2;
    g.beginPath(); g.arc(0, -125 + b, 13, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = INK; g.font = 'bold 16px system-ui'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText('!', 0, -124 + b);
  }
  g.restore();
}

export function drawPearl(g: CanvasRenderingContext2D, x: number, y: number, t: number, lights: [number, number, number, string][]) {
  g.save();
  g.translate(x, y);
  g.lineJoin = 'round';
  g.strokeStyle = INK; g.lineWidth = 2;
  const open = 0.35 + 0.15 * Math.sin(t * 1.5 + x);
  g.fillStyle = '#c9b8e8';
  g.beginPath(); g.moveTo(-18, 6); g.quadraticCurveTo(0, 18, 18, 6); g.quadraticCurveTo(0, 0, -18, 6); g.fill(); g.stroke();
  g.save(); g.translate(-18, 6); g.rotate(-open);
  g.beginPath(); g.moveTo(0, 0); g.quadraticCurveTo(18, -20, 36, 0); g.quadraticCurveTo(18, -6, 0, 0); g.fill(); g.stroke(); g.restore();
  const glint = 0.6 + 0.4 * Math.sin(t * 3 + x);
  const pg = g.createRadialGradient(-2, -2, 1, 0, 0, 9);
  pg.addColorStop(0, '#ffffff'); pg.addColorStop(1, '#e9e2ff');
  g.fillStyle = pg;
  g.beginPath(); g.arc(0, 2, 7.5, 0, Math.PI * 2); g.fill(); g.stroke();
  g.fillStyle = `rgba(255,255,255,${glint})`;
  g.beginPath(); g.arc(-2.5, -0.5, 2.2, 0, Math.PI * 2); g.fill();
  g.restore();
  lights.push([x, y, 60, '#f3eaff']);
}

/** A treasure chest standing at (x, y): wood (tier 1), sea-glass silver (2) or gold (3); open once looted. */
export function drawChest(g: CanvasRenderingContext2D, x: number, y: number, tier: 1 | 2 | 3, open: boolean, t: number, near: boolean) {
  const body = tier === 3 ? '#e0a92a' : tier === 2 ? '#5f8f9c' : '#8a5a35';
  const band = tier === 3 ? '#fff1a8' : tier === 2 ? '#cfeff5' : '#d8b25a';
  const s = tier === 3 ? 1.35 : tier === 2 ? 1.15 : 1;
  g.save();
  g.translate(x, y);
  g.scale(s, s);
  g.lineJoin = 'round'; g.strokeStyle = INK; g.lineWidth = 2.5;
  if (open) g.globalAlpha = 0.75;
  g.fillStyle = body;
  g.beginPath(); g.roundRect(-24, -26, 48, 26, 4); g.fill(); g.stroke();
  if (open) {
    g.save(); g.translate(-24, -26); g.rotate(-1.1);
    g.beginPath(); g.roundRect(0, -16, 48, 16, [8, 8, 2, 2]); g.fill(); g.stroke(); g.restore();
    g.fillStyle = '#1e1a16'; g.beginPath(); g.ellipse(0, -26, 20, 5, 0, 0, Math.PI * 2); g.fill();
  } else {
    g.beginPath(); g.roundRect(-24, -42, 48, 17, [10, 10, 2, 2]); g.fill(); g.stroke();
    g.fillStyle = band;
    for (const bx of [-15, 11]) { g.fillRect(bx, -42, 4, 42); g.strokeRect(bx, -42, 4, 42); }
    g.beginPath(); g.roundRect(-5, -30, 10, 10, 2); g.fill(); g.stroke();
    // A glint now and then.
    const glint = Math.max(0, Math.sin(t * 2 + x * 0.01));
    if (glint > 0.85) { g.fillStyle = '#fff'; g.beginPath(); g.arc(14, -38, 2.5, 0, Math.PI * 2); g.fill(); }
  }
  g.restore();
  if (near && !open) {
    g.save();
    g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = 3; g.setLineDash([6, 6]); g.lineDashOffset = -t * 20;
    g.beginPath(); g.arc(x, y - 22 * s, 36 * s, 0, Math.PI * 2); g.stroke();
    g.restore();
  }
}

/** A sealed temple door across a shaft: carved stone with glowing runes (it opens once the inscription is read). */
export function drawGate(g: CanvasRenderingContext2D, r: { x: number; y: number; w: number; h: number }, t: number, near: boolean) {
  g.save();
  g.lineJoin = 'round'; g.strokeStyle = INK; g.lineWidth = 3;
  g.fillStyle = '#56706d';
  g.beginPath(); g.roundRect(r.x, r.y, r.w, r.h, 10); g.fill(); g.stroke();
  g.strokeStyle = 'rgba(30,38,51,0.4)'; g.lineWidth = 2;
  g.beginPath(); g.moveTo(r.x + r.w / 2, r.y + 6); g.lineTo(r.x + r.w / 2, r.y + r.h - 6); g.stroke();
  for (const fx of [0.25, 0.75]) { g.beginPath(); g.arc(r.x + r.w * fx, r.y + r.h / 2, 22, 0, Math.PI * 2); g.stroke(); }
  const glow = 0.55 + 0.45 * Math.sin(t * 2.2);
  g.strokeStyle = `rgba(126,249,255,${glow})`; g.lineWidth = 3;
  const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
  for (let i = 0; i < 6; i++) {
    const x = r.x + 24 + i * ((r.w - 48) / 5), y = cy + (i % 2 ? 26 : -26);
    g.beginPath(); g.moveTo(x - 7, y - 7); g.lineTo(x + 7, y + 7); g.moveTo(x + 7, y - 7); g.lineTo(x, y); g.stroke();
  }
  g.beginPath(); g.arc(cx, cy, 12, 0, Math.PI * 2); g.stroke();
  g.restore();
  if (near) {
    g.save();
    g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = 3; g.setLineDash([6, 6]); g.lineDashOffset = -t * 20;
    g.strokeRect(r.x - 10, r.y - 10, r.w + 20, r.h + 20);
    g.restore();
  }
}
